import React, { useRef, useState, useEffect } from 'react';
import { Card, Form, Row, Col, ButtonGroup, ProgressBar } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Button } from '@components';
import { FILE_MANAGER_API, BaseGatewayImage } from '@app/utils/apiUrl';
import { getToken } from '@app/utils/helper';
import { acceptDoc } from '@app/utils/define';

// --- CÁC HÀM TIỆN ÍCH HỖ TRỢ UPLOAD FILE ---
const isImageFile = (filePath = '') => {
    return /\.(jpeg|jpg|gif|png|jpe)$/i.test(filePath);
};

const uploadFileToServer = async ({ fileUpload, setCompleted }) => {
    const token = await getToken();
    const config = {
        onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setCompleted(percentCompleted);
        },
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `bearer ${token}` }
    };
    const data = new FormData();
    data.append('file', fileUpload);
    return axios.post(FILE_MANAGER_API.UPLOAD, data, config);
};

// --- COMPONENT FILE MINH CHỨNG ---
const FileMinhChungField = ({ value, onChange, disabled = false }) => {
    const inputUpload = useRef(null);
    const [completed, setCompleted] = useState(0);
    const [fileInfo, setFileInfo] = useState({ id: '', filePath: '', name: '' });

    useEffect(() => {
        setFileInfo({ id: value?.id || '', filePath: value?.filePath || '', name: value?.name || '' });
    }, [value]);

    const handleUploadFile = async (event) => {
        if (disabled) return;
        const fileUpload = event.target.files?.[0];
        if (!fileUpload) return;
        const fileSize = fileUpload.size / 1024 / 1024;
        if (fileSize > 20) {
            toast.error('Kích thước file quá lớn, hãy thử lại với tệp nhỏ hơn 20MB');
            return;
        }

        try {
            const result = await uploadFileToServer({ fileUpload, setCompleted });
            const uploaded = result?.data?.[0];
            if (!uploaded) {
                toast.error('Không nhận được dữ liệu file sau khi tải lên');
                setCompleted(0);
                return;
            }
            const normalized = {
                id: uploaded.id || uploaded.Id || '',
                filePath: uploaded.filePath || uploaded.FilePath || '',
                name: uploaded.name || uploaded.Name || fileUpload.name
            };
            setFileInfo(normalized);
            onChange(normalized);
            setCompleted(0);
            toast.success('Tải file thành công!');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Tải file thất bại');
            setCompleted(0);
        } finally {
            if (inputUpload.current) inputUpload.current.value = '';
        }
    };

    const handleRemove = () => {
        if (disabled) return;
        const emptyFile = { id: '', filePath: '', name: '' };
        setFileInfo(emptyFile);
        onChange(emptyFile);
    };

    const handleOpenFile = () => {
        if (fileInfo.filePath) {
            window.open(`${BaseGatewayImage}${fileInfo.filePath}`, '_blank');
        } else if (fileInfo.id) {
            window.open(`${BaseGatewayImage}/api/v1/FileManager/${fileInfo.id}`, '_blank');
        }
    };

    const hasFile = !!(fileInfo.filePath || fileInfo.id);
    const isUploaded = hasFile && completed === 0;

    const renderImagePreview = () => {
        if (!fileInfo.filePath || !isImageFile(fileInfo.filePath)) return null;
        return (
            <div style={{
                border: '1px solid #dee2e6', borderRadius: '14px',
                padding: '12px', background: '#fff', marginBottom: '10px', textAlign: 'left'
            }}>
                <img src={`${BaseGatewayImage}${fileInfo.filePath}`} alt={fileInfo.name || 'file minh chứng'}
                    style={{ maxWidth: '240px', maxHeight: '180px', objectFit: 'contain', borderRadius: '8px' }} />
            </div>
        );
    };

    const displayValue = fileInfo.name ||
                        (fileInfo.filePath ? fileInfo.filePath.split('/').pop() : '') ||
                        (fileInfo.id ? 'Đã có tệp đính kèm' : '');

    return (
        <>
            {renderImagePreview()}
            <div style={{ border: '1px solid #dee2e6', borderRadius: '14px', padding: '16px', background: '#fff' }}>
                <div className="d-flex flex-wrap align-items-center mb-3">
                    {!disabled && (
                        <Button type="button" theme="primary" onClick={() => inputUpload.current?.click()}>Upload</Button>
                    )}
                    {!disabled && <div className="mx-3 text-muted">Dung lượng tối đa: 20MB</div>}
                    <Button type="button" theme="secondary" onClick={handleOpenFile} disabled={!hasFile}>
                        Xem
                    </Button>
                    {!disabled && (
                        <Button type="button" theme="danger" onClick={handleRemove} disabled={!hasFile} style={{ marginLeft: '8px' }}>Xóa</Button>
                    )}
                </div>
                {!disabled && <Form.Control type="file" hidden accept={acceptDoc} ref={inputUpload} onChange={handleUploadFile} />}
                <Form.Control value={displayValue} readOnly placeholder="Chưa có file minh chứng" />
                {!disabled && <ProgressBar className="mt-3" style={{ display: isUploaded ? 'none' : 'flex' }} variant="success" animated now={completed} />}
            </div>
        </>
    );
};

// --- SCHEMA VALIDATE ---
const schema = yup.object().shape({
    soChungChi: yup
        .string()
        .required('Số chứng chỉ không được để trống')
        .max(255, 'Số chứng chỉ không được vượt quá 255 ký tự'),
    tenChungChi: yup
        .string()
        .required('Tên chứng chỉ không được để trống')
        .max(255, 'Tên chứng chỉ không được vượt quá 255 ký tự'),
    loaiNgoaiNgu: yup
        .string()
        .required('Vui lòng chọn loại ngoại ngữ'),
    trinhDo: yup
        .string()
        .required('Trình độ không được để trống')
        .max(255, 'Trình độ không được vượt quá 255 ký tự'),
    ngayCap: yup
        .string()
        .required('Ngày cấp không được để trống'),
    quocGiaCap: yup
        .string()
        .required('Quốc gia cấp không được để trống')
        .max(255, 'Quốc gia cấp không được vượt quá 255 ký tự'),
});

// --- MAIN COMPONENT ---
const ChungChiNgoaiNguForm = ({
    title,
    submitText,
    initialValues,
    onSubmit,
    onCancel,
    isSubmitting
}) => {
    return (
        <Card style={{ borderRadius: '16px' }}>
            <Card.Header as="h5" style={{ padding: '16px 20px', fontWeight: 700, background: '#fff', borderBottom: '1px solid #ebedf2' }}>
                {title}
            </Card.Header>
            <Card.Body style={{ background: '#fff', padding: '24px' }}>
                <Formik
                    enableReinitialize
                    validationSchema={schema}
                    onSubmit={onSubmit}
                    initialValues={initialValues}
                >
                    {({ handleSubmit, handleChange, values, errors, touched, setFieldValue }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <h6 style={{ color: '#007bff', fontWeight: 600, marginBottom: '20px' }}>Thông tin chứng chỉ ngoại ngữ</h6>

                            {/* DÒNG 1 */}
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-500">Tên chứng chỉ <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            as="select"
                                            name="tenChungChi"
                                            value={values.tenChungChi}
                                            onChange={handleChange}
                                            isInvalid={touched.tenChungChi && !!errors.tenChungChi}
                                        >
                                            <option value="">-- Chọn tên chứng chỉ --</option>
                                            <option value="IELTS">IELTS</option>
                                            <option value="TOEIC">TOEIC</option>
                                            <option value="TOEIC iBT">TOEIC iBT</option>
                                            <option value="Cambridge English">Cambridge English</option>
                                            <option value="APTIS">APTIS</option>
                                            <option value="VSTEP">VSTEP</option>
                                            <option value="PTE">PTE</option>
                                            <option value="JLPT">JLPT</option>
                                            <option value="TOPIK">TOPIK</option>
                                            <option value="DALF">DALF</option>
                                            <option value="DELF">DELF</option>
                                            <option value="TestDaF">TestDaF</option>
                                            <option value="HSK">HSK</option>
                                            <option value="Khác">Khác</option>
                                        </Form.Control>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.tenChungChi}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-500">Số chứng chỉ <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            name="soChungChi"
                                            value={values.soChungChi}
                                            onChange={handleChange}
                                            isInvalid={touched.soChungChi && !!errors.soChungChi}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.soChungChi}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* DÒNG 2 */}
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-500">Loại ngoại ngữ <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            as="select"
                                            name="loaiNgoaiNgu"
                                            value={values.loaiNgoaiNgu}
                                            onChange={handleChange}
                                            isInvalid={touched.loaiNgoaiNgu && !!errors.loaiNgoaiNgu}
                                        >
                                            <option value="">-- Chọn loại ngoại ngữ --</option>
                                            <option value="Tiếng Anh">Tiếng Anh</option>
                                            <option value="Tiếng Đức">Tiếng Đức</option>
                                            <option value="Tiếng Pháp">Tiếng Pháp</option>
                                            <option value="Tiếng Nhật">Tiếng Nhật</option>
                                            <option value="Tiếng Trung Quốc">Tiếng Trung Quốc</option>
                                            <option value="Tiếng Nga">Tiếng Nga</option>
                                            <option value="Tiếng Tây Ba Nha">Tiếng Tây Ba Nha</option>
                                            <option value="Khác">Khác</option>
                                        </Form.Control>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.loaiNgoaiNgu}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-500">Trình độ <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            name="trinhDo"
                                            value={values.trinhDo}
                                            onChange={handleChange}
                                            isInvalid={touched.trinhDo && !!errors.trinhDo}
                                            placeholder="Ví dụ: A1, A2, HSK4,..."
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.trinhDo}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* DÒNG 3 */}
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-500">Điểm số</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            name="diemSo"
                                            value={values.diemSo}
                                            onChange={handleChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-500">Ngày cấp <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="ngayCap"
                                            value={values.ngayCap ? values.ngayCap.split('T')[0] : ''}
                                            onChange={handleChange}
                                            isInvalid={touched.ngayCap && !!errors.ngayCap}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.ngayCap}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* DÒNG 4 */}
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-500">Ngày hết hạn</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="ngayHetHan"
                                            value={values.ngayHetHan ? values.ngayHetHan.split('T')[0] : ''}
                                            onChange={handleChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-500">Thời hạn</Form.Label>
                                        <Form.Control
                                            name="thoiHan"
                                            value={values.thoiHan}
                                            onChange={handleChange}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* DÒNG 5 */}
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-500">Đơn vị cấp</Form.Label>
                                        <Form.Control
                                            name="donViCap"
                                            value={values.donViCap}
                                            onChange={handleChange}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-500">Quốc gia cấp <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            as="select"
                                            name="quocGiaCap"
                                            value={values.quocGiaCap}
                                            onChange={handleChange}
                                            isInvalid={touched.quocGiaCap && !!errors.quocGiaCap}
                                        >
                                            <option value="">-- Chọn quốc gia cấp --</option>
                                            <option value="Anh">Anh</option>
                                            <option value="Đức">Đức</option>
                                            <option value="Pháp">Pháp</option>
                                            <option value="Nhật">Nhật</option>
                                            <option value="Trung Quốc">Trung Quốc</option>
                                            <option value="Nga">Nga</option>
                                            <option value="Tây Ba Nha">Tây Ba Nha</option>
                                            <option value="Việt Nam">Việt Nam</option>
                                            <option value="Khác">Khác</option>
                                        </Form.Control>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.quocGiaCap}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>

                            {/* FILE MINH CHỨNG */}
                            <Form.Group className="mb-3 mt-2">
                                <Form.Label className="fw-500">File minh chứng</Form.Label>
                                <FileMinhChungField
                                    value={{
                                        id: values.fileMinhChungId,
                                        filePath: values.fileMinhChungPath,
                                        name: values.fileMinhChungName
                                    }}
                                    onChange={(file) => {
                                        setFieldValue('fileMinhChungId', file?.id || '');
                                        setFieldValue('fileMinhChungPath', file?.filePath || '');
                                        setFieldValue('fileMinhChungName', file?.name || '');
                                    }}
                                />
                            </Form.Group>

                            {/* GHI CHÚ */}
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-500">Ghi chú</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="ghiChu"
                                    value={values.ghiChu}
                                    onChange={handleChange}
                                />
                            </Form.Group>

                            {/* BUTTONS */}
                            <Form.Group as={Col} className="mb-2 mt-4">
                                <div className="d-flex justify-content-center">
                                    <ButtonGroup>
                                        <Button type="submit" theme="primary" disabled={isSubmitting}>
                                            {isSubmitting ? 'Đang xử lý...' : submitText}
                                        </Button>
                                        <div style={{ width: '15px' }}></div>
                                        <Button theme="secondary" onClick={onCancel} type="button">
                                            Hủy
                                        </Button>
                                    </ButtonGroup>
                                </div>
                            </Form.Group>

                        </Form>
                    )}
                </Formik>
            </Card.Body>
        </Card>
    );
};

export default ChungChiNgoaiNguForm;
